import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import Button from 'shared/components/Button';
import { PlusButton, TrashButton } from 'shared/components/IconButtons';
import { useThemeContext } from 'shared/context/themeContext';
import { dark as darkColors, light as lightColors } from 'shared/theme/colorTokens';
import { useSnackbar } from 'notistack';
import { FC, useState } from "react";
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { sendRecipietToEmails } from '../services/recipietService';

interface SendReceiptModalProps {
  onClose: () => void;
  reserveId: string;
  clientId: number;
}

const SendReceiptFormSchema = z.object({
  emails: z.record(z.string(), z.string({
    required_error: 'Email requerido'
  }).email({
    message: 'Email inválido',
  })),
});

type SendReceiptForm = z.infer<typeof SendReceiptFormSchema>;

const SendReceiptModal: FC<SendReceiptModalProps> = ({
  clientId,
  onClose,
  reserveId
}) => {
  const snackbar = useSnackbar();
  const { theme } = useThemeContext();
  const isDark = theme === 'dark';
  const [emailsIds, setEmailsIds] = useState<string[]>([uuidv4()]);

  const { control, handleSubmit, unregister, formState: { isDirty, isSubmitting } } = useForm<SendReceiptForm>({
    defaultValues: {
      emails: {
        [emailsIds[0]]: ''
      }
    },
    resolver: zodResolver(SendReceiptFormSchema),
  });

  const handleRemoveEmail = (emailId: string) => {
    unregister(`emails.${emailId}`);
    setEmailsIds(emailsIds.filter((id) => id !== emailId));
  };

  const onSubmit = async (data: SendReceiptForm) => {
    try {
      const emails = Object.values(data.emails);
      await sendRecipietToEmails(emails, clientId, reserveId);
      snackbar.enqueueSnackbar('Recibo enviado', {
        variant: 'info'
      });
      onClose();
    } catch {
      snackbar.enqueueSnackbar('Error al enviar recibo', {
        variant: 'error'
      });
    }
  }

  const handleModaleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  }

  return (
    <Dialog
      open
      onClose={handleModaleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: isDark ? darkColors.surface : lightColors.surface
        }
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          Enviar Recibo
        </DialogTitle>
        <DialogContent dividers>
          <div className="w-full flex flex-col gap-6">
            <p className="text-sm text-ink dark:text-ink-dark">
              <span className="font-semibold">Reserva:</span> {reserveId}
            </p>
            <div className="w-full flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-ink dark:text-ink-dark">
                  Destinatarios
                </span>
                <PlusButton
                  onClick={() => setEmailsIds((current) => [...current, uuidv4()])}
                />
              </div>
              {emailsIds.map((id, index) => {
                if (index === 0) {
                  return (
                    <div className='flex' key={id}>
                      <Controller
                        control={control}
                        name={`emails.${id}`}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            error={!!error?.message}
                            helperText={error?.message}
                            {...field}
                            className="w-full"
                            label="Email"
                            placeholder="Email del destinatario del recibo"
                            variant="outlined"
                          />
                        )}
                      />
                      <div className='w-20' />
                    </div>
                  )
                }
                return (
                  <div className='flex items-center' key={id}>
                    <Controller
                      control={control}
                      name={`emails.${id}`}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          error={!!error?.message}
                          helperText={error?.message}
                          {...field}
                          className="w-full"
                          label="Email"
                          placeholder="Email del destinatario del recibo"
                          variant="outlined"
                        />
                      )}
                    />
                    <div className='w-20 flex justify-end'>
                      <TrashButton onClick={() => handleRemoveEmail(id)} />
                    </div>
                  </div>
                )
              })}
            </div>

          </div>
        </DialogContent>
        <DialogActions>
          <div className='py-2 flex gap-x-4 pr-4'>
            <Button
              type="button"
              onClick={onClose}
              variant="secondary"
              size="sm"
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type='submit'
              size="sm"
              disabled={!isDirty || isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar Recibo'}
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  )
};

export default SendReceiptModal;
